import { Trans } from '@lingui/react/macro'
import { Badge } from 'designSystem/components/Badge'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from 'designSystem/components/Resizable'
import { Text } from 'designSystem/components/Text'
import { useMediaQuery } from 'designSystem/components/useMediaQuery'
import { useIsTouchMobile } from 'designSystem/hooks/useIsTouchDevice'
import {
  type DocumentTableRow,
  formatDocumentDisplayTitle,
} from 'features/knowledgeDocumentViewer'
import { PreviewDocumentDetailsSection } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/PreviewDocumentDetailsSection'
import { StackedMetadataEditSections } from 'features/knowledgeDocumentViewer/components/metadata/StackedMetadataEditSections'
import { CitedTextBar } from 'features/knowledgeDocumentViewer/components/viewer/search/CitedTextBar'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePreviewGraph } from 'features/knowledgeDocumentViewer/graph/useDocumentGraph'
import { useFragmentHighlight } from 'features/knowledgeDocumentViewer/hooks/useFragmentHighlight'
import { useResolvedDocumentPageImage } from 'features/knowledgeDocumentViewer/hooks/useResolvedDocumentPageImage'
import { useStackedMetadataEditControllers } from 'features/knowledgeDocumentViewer/hooks/useStackedMetadataEditControllers'
import { useSystemDocumentByBqId } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import { citedQuotesFragment } from 'features/knowledgeDocumentViewer/utils/citedQuotesFragment'
import { getPageParsedRichText } from 'features/knowledgeDocumentViewer/utils/pageMetadataFields'
import {
  pageRevisionMemberToTableRow,
  type RevisionFamilyMemberView,
} from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import { isCmmsRecordId } from 'features/knowledgeDocumentViewer/workOrders/workOrder'
import { WorkOrderViewer } from 'features/knowledgeDocumentViewer/workOrders/WorkOrderViewer'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { PHONE_LANDSCAPE_QUERY } from 'utils/mobileLayout'

import {
  DocumentPreviewMeta,
  hasDocumentPreviewMetadata,
} from './DocumentPreviewMeta'
import { resolveDocumentPreviewSummarySection } from './documentPreviewSummary'
import { DocumentPreviewViewer } from './DocumentPreviewViewer'
import { PreviewPageLightbox } from './PreviewPageLightbox'
import { createPreviewSplits, updatePreviewSplit } from './previewSplitPolicy'

const ContentRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  --edge-fade-from: var(--card);
`

const SplitArea = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
`

const ViewerContainer = styled.div`
  position: relative;
  height: 100%;
  min-height: 5rem;
  overflow: hidden;
`

const ScrollableContent = styled.div<{ $fillAvailable?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: ${({ $fillAvailable }) => ($fillAvailable ? 'hidden' : 'auto')};
`

const TitleSection = styled.div<{ $hasDivider: boolean }>`
  display: flex;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: ${({ $hasDivider }) =>
    $hasDivider ? '1px solid var(--border)' : 'none'};
`

const MetaSection = styled.div<{ $fillAvailable?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-top: ${({ $fillAvailable }) =>
    $fillAvailable ? 'none' : '1px solid var(--border)'};
  border-bottom: ${({ $fillAvailable }) =>
    $fillAvailable ? 'none' : '1px solid var(--border)'};
  padding: ${({ $fillAvailable }) =>
    $fillAvailable ? '1.25rem 0 0' : '1.25rem 0'};
  ${({ $fillAvailable }) =>
    $fillAvailable
      ? `
    flex: 1;
    min-height: 0;
    overflow-y: hidden;
  `
      : ''}
`

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
`

const SummaryText = styled.div`
  white-space: pre-wrap;
`

const KeywordsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const OpenButtonWrapper = styled.div`
  flex-shrink: 0;
  padding: 1rem;
`

const STORAGE_KEY = 'documentSearch_previewViewerHeight:v2'
const DEFAULT_VIEWER_HEIGHT_PERCENT = 55
const MIN_VIEWER_HEIGHT_PERCENT = 15
const MAX_VIEWER_HEIGHT_PERCENT = 75
const VIEWER_PANEL_ID = 'preview-viewer'
const DETAILS_PANEL_ID = 'preview-details'

function writeStoredViewerHeight(percent: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(percent))
  } catch {
    /* localStorage unavailable */
  }
}

function readStoredViewerHeight(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = Number(stored)
      if (
        !Number.isNaN(parsed) &&
        parsed >= MIN_VIEWER_HEIGHT_PERCENT &&
        parsed <= MAX_VIEWER_HEIGHT_PERCENT
      )
        return parsed
    }
  } catch {
    /* localStorage unavailable */
  }
  return DEFAULT_VIEWER_HEIGHT_PERCENT
}

export interface DocumentPreviewContentProps {
  document: DocumentTableRow
  initialPage?: number
  citedQuotes?: string[]
  onDismissCitedQuotes?: () => void
  onOpen?: (doc: DocumentTableRow) => void
  pageMetadataEnabled: boolean
  canEditPageMetadata?: boolean
  includeInternalIds?: boolean
  onMetadataEditingChange?: (isEditing: boolean) => void
  onSwitchPreviewDocument?: (doc: DocumentTableRow, pageNum: number) => void
  onOpenDocumentInNewTab?: (doc: DocumentTableRow, pageNum: number) => void
}

export function DocumentPreviewContent(props: DocumentPreviewContentProps) {
  return isCmmsRecordId(props.document.id) ? (
    <WorkOrderViewer
      key={props.document.id}
      documentId={props.document.id}
      initialPage={props.initialPage}
      citedQuotes={props.citedQuotes}
      expectedVersion={props.document.cmmsVersion}
      onDismissCitedQuotes={props.onDismissCitedQuotes}
    />
  ) : (
    <SourceDocumentPreviewContent {...props} />
  )
}

const SourceDocumentPreviewContent: React.FC<DocumentPreviewContentProps> = ({
  document,
  initialPage = 1,
  citedQuotes,
  onDismissCitedQuotes,
  onOpen,
  pageMetadataEnabled,
  canEditPageMetadata = false,
  includeInternalIds = false,
  onMetadataEditingChange,
  onSwitchPreviewDocument,
  onOpenDocumentInNewTab,
}) => {
  const { siteID } = useKnowledgeDocumentDataContext()
  const totalPages = document.numPages ?? 1
  const clampedInitialPage = Math.min(Math.max(initialPage, 1), totalPages)
  const [currentPreviewPage, setCurrentPreviewPage] =
    useState(clampedInitialPage)

  useEffect(() => {
    setCurrentPreviewPage(clampedInitialPage)
  }, [document.id, clampedInitialPage])

  const isTouchMobile = useIsTouchMobile()
  const isLandscapePhone = useMediaQuery(PHONE_LANDSCAPE_QUERY)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const openLightbox = useCallback(() => setIsLightboxOpen(true), [])
  const closeLightbox = useCallback(() => setIsLightboxOpen(false), [])

  useEffect(() => {
    setIsLightboxOpen(false)
  }, [document.id])

  const pageMetadataVisible = pageMetadataEnabled
  const canEdit = canEditPageMetadata && pageMetadataVisible
  const previewBqDocumentId = document.bqDocumentId ?? document.id
  const citedFragment = useMemo(
    () =>
      citedQuotesFragment(
        clampedInitialPage,
        citedQuotes,
        document.cmmsVersion
      ),
    [clampedInitialPage, citedQuotes, document.cmmsVersion]
  )
  const citedLocation = useFragmentHighlight(
    previewBqDocumentId,
    siteID,
    citedFragment
  )
  const isOnCitedPage = currentPreviewPage === clampedInitialPage
  const citedBoxes = isOnCitedPage
    ? citedLocation.hit?.bounding_boxes
    : undefined
  const { document: systemDocument, isLoading: isSystemDocumentLoading } =
    useSystemDocumentByBqId(previewBqDocumentId)
  const {
    showDocumentDetails,
    showPageDetails,
    isEditingMetadata,
    documentUserEdit,
    isDocumentUserEditLoading,
    pageEntry,
    isPageLoading,
    pageUserEdit,
    isPageUserEditLoading,
    isSavingDocumentEdit,
    saveDocumentMetadata,
    saveDocumentMetadataFamily,
    isSavingPageEdit,
    savePageMetadata,
    savePageMetadataFamily,
    handleDocumentEditingChange,
    handlePageEditingChange,
  } = useStackedMetadataEditControllers({
    bqDocumentId: document.bqDocumentId,
    systemDocumentEntryId: document.systemEntryId,
    systemDocument,
    pageNum: currentPreviewPage,
    userEditsEnabled: pageMetadataVisible,
    pageDetailsEnabled: pageMetadataVisible,
    resetKey: document.id,
    onEditingChange: onMetadataEditingChange,
  })

  const canActOnRevisionFamily =
    onSwitchPreviewDocument !== undefined &&
    onOpenDocumentInNewTab !== undefined

  const handleSelectVersion = useCallback(
    (member: RevisionFamilyMemberView) => {
      if (!canActOnRevisionFamily) return
      const row = pageRevisionMemberToTableRow(member)
      if (!row) return
      onSwitchPreviewDocument(row, member.pageNum)
    },
    [canActOnRevisionFamily, onSwitchPreviewDocument]
  )

  const handleOpenVersionInNewTab = useCallback(
    (member: RevisionFamilyMemberView) => {
      if (!canActOnRevisionFamily) return
      const row = pageRevisionMemberToTableRow(member)
      if (!row) return
      onOpenDocumentInNewTab(row, member.pageNum)
    },
    [canActOnRevisionFamily, onOpenDocumentInNewTab]
  )

  const graph = usePreviewGraph(
    {
      systemDocumentEntryId: document.systemEntryId,
      bqDocumentId: document.bqDocumentId,
    },
    currentPreviewPage
  )

  const { thumbnailUrl, isThumbLoading, hiResUrl } =
    useResolvedDocumentPageImage(document.systemEntryId, siteID, {
      pageNumber: currentPreviewPage,
      skipHiRes: false,
      knownHosts: {
        systemDocumentEntryId: document.systemEntryId,
        filename: document.filename,
        bqDocumentId: document.bqDocumentId ?? document.id,
        cantileverDocumentEntryId: document.cantileverDocumentEntryId,
        hasHydratedDocumentHosts: true,
      },
    })

  const [initialSplits] = useState(() =>
    createPreviewSplits(readStoredViewerHeight())
  )
  const splitPercents = useRef(initialSplits)
  const viewerPercent = isLandscapePhone
    ? splitPercents.current.landscape
    : splitPercents.current.standard
  const defaultSplitLayout = {
    [VIEWER_PANEL_ID]: viewerPercent,
    [DETAILS_PANEL_ID]: 100 - viewerPercent,
  }
  const handleSplitLayoutChanged = useCallback(
    (layout: Record<string, number>) => {
      const percent = layout[VIEWER_PANEL_ID]
      if (percent === undefined) return
      const next = updatePreviewSplit(
        splitPercents.current,
        isLandscapePhone,
        percent
      )
      splitPercents.current = next.splits
      if (next.persistPercent !== null)
        writeStoredViewerHeight(next.persistPercent)
    },
    [isLandscapePhone]
  )

  const pageParsed = getPageParsedRichText(pageEntry)
  const summary = pageMetadataVisible
    ? pageParsed.summary
    : (document.summary ?? '')
  const keywords = pageMetadataVisible ? pageParsed.keywords : document.keywords
  const hasDocMetadata = hasDocumentPreviewMetadata(document)
  const showStackedDetails = pageMetadataVisible
  const showLegacyDocMeta = !pageMetadataVisible && hasDocMetadata
  const showMetaSection = showStackedDetails || showLegacyDocMeta
  const { showSummarySection, showNoPageSummary } =
    resolveDocumentPreviewSummarySection({
      showPageMetadata: pageMetadataVisible,
      isPageLoading,
      summary,
      keywords,
    })
  const hideChromeForEdit = canEdit && isEditingMetadata
  const offersFullscreen = isTouchMobile && !hideChromeForEdit
  const handleToggleFullscreen = offersFullscreen ? openLightbox : undefined

  useEffect(() => {
    if (hideChromeForEdit) setIsLightboxOpen(false)
  }, [hideChromeForEdit])

  const showDocumentDetailsWhileStacked =
    showStackedDetails && showDocumentDetails
  const showPageDetailsWhileStacked = showStackedDetails && showPageDetails
  const showSummaryWhileViewing = showSummarySection && !hideChromeForEdit
  const canRenderStackedDocumentDetails =
    systemDocument !== undefined || Boolean(document.bqDocumentId)
  const stackedDocumentFallback =
    canRenderStackedDocumentDetails ? undefined : (
      <PreviewDocumentDetailsSection
        document={document}
        includeInternalIds={includeInternalIds}
        hideRevision
      />
    )

  const displayTitle = formatDocumentDisplayTitle(
    document.documentTitle,
    document.title
  )

  return (
    <ContentRoot>
      <SplitArea>
        <ResizablePanelGroup
          key={isLandscapePhone ? 'landscape' : 'standard'}
          orientation='vertical'
          defaultLayout={defaultSplitLayout}
          onLayoutChanged={handleSplitLayoutChanged}>
          <ResizablePanel
            id={VIEWER_PANEL_ID}
            minSize={isLandscapePhone ? '40%' : `${MIN_VIEWER_HEIGHT_PERCENT}%`}
            maxSize={`${MAX_VIEWER_HEIGHT_PERCENT}%`}>
            <ViewerContainer>
              {citedFragment && isOnCitedPage && onDismissCitedQuotes ? (
                <CitedTextBar
                  isLocating={citedLocation.isLoading}
                  isLocated={citedLocation.hit !== null}
                  onDismiss={onDismissCitedQuotes}
                  placement='top-left'
                />
              ) : null}
              {isLightboxOpen ? null : (
                <DocumentPreviewViewer
                  thumbnailUrl={thumbnailUrl ?? null}
                  hiResUrl={hiResUrl ?? null}
                  isThumbLoading={isThumbLoading}
                  alt={displayTitle}
                  documentId={document.id}
                  currentPage={currentPreviewPage}
                  totalPages={totalPages}
                  highlightBoxes={citedBoxes}
                  graph={graph}
                  onPageChange={setCurrentPreviewPage}
                  pageNavigationDisabled={hideChromeForEdit}
                  showResetZoom={!offersFullscreen}
                  onToggleFullscreen={handleToggleFullscreen}
                />
              )}
            </ViewerContainer>
          </ResizablePanel>
          <ResizableHandle variant='grabber' />
          <ResizablePanel id={DETAILS_PANEL_ID}>
            <ScrollableContent $fillAvailable={hideChromeForEdit}>
              {!hideChromeForEdit ? (
                <TitleSection $hasDivider={!showMetaSection}>
                  <Text as='h2' variant='heading' weight='semibold'>
                    {displayTitle}
                  </Text>
                </TitleSection>
              ) : null}
              {showMetaSection ? (
                <MetaSection $fillAvailable={hideChromeForEdit}>
                  {showStackedDetails ? (
                    <StackedMetadataEditSections
                      showDocumentDetails={showDocumentDetailsWhileStacked}
                      showPageDetails={showPageDetailsWhileStacked}
                      systemDocument={systemDocument}
                      documentUserEdit={documentUserEdit}
                      isDocumentLoading={
                        isSystemDocumentLoading || isDocumentUserEditLoading
                      }
                      canEditDocument={
                        canEdit && Boolean(document.bqDocumentId)
                      }
                      isSavingDocumentEdit={isSavingDocumentEdit}
                      pageNum={currentPreviewPage}
                      pageEntry={pageEntry}
                      pageUserEdit={pageUserEdit}
                      isPageLoading={isPageLoading || isPageUserEditLoading}
                      canEditPage={canEdit && Boolean(document.bqDocumentId)}
                      isSavingPageEdit={isSavingPageEdit}
                      currentBqDocumentId={previewBqDocumentId}
                      currentFilename={document.filename}
                      onSaveDocument={saveDocumentMetadata}
                      onSaveDocumentFamily={saveDocumentMetadataFamily}
                      onSavePage={savePageMetadata}
                      onSavePageFamily={savePageMetadataFamily}
                      onDocumentEditingChange={handleDocumentEditingChange}
                      onPageEditingChange={handlePageEditingChange}
                      onSelectVersion={
                        canActOnRevisionFamily ? handleSelectVersion : undefined
                      }
                      onOpenInNewTab={
                        canActOnRevisionFamily
                          ? handleOpenVersionInNewTab
                          : undefined
                      }
                      includeInternalIds={includeInternalIds}
                      documentFallback={stackedDocumentFallback}
                      fillAvailable={hideChromeForEdit}
                      showRevisionDropdown={canActOnRevisionFamily}
                      hideDocumentRevision
                    />
                  ) : (
                    <Box paddingX='xl'>
                      <DocumentPreviewMeta document={document} />
                    </Box>
                  )}
                </MetaSection>
              ) : null}
              {showSummaryWhileViewing ? (
                <SummarySection>
                  {summary ? (
                    <SummaryText>
                      <Text as='p' variant='body' color='muted'>
                        {summary}
                      </Text>
                    </SummaryText>
                  ) : showNoPageSummary ? (
                    <Text as='p' variant='body' color='muted' italic>
                      <Trans>No page summary</Trans>
                    </Text>
                  ) : null}
                  {keywords.length > 0 ? (
                    <KeywordsWrapper>
                      {keywords.map((keyword, index) => (
                        <Badge key={`${index}-${keyword}`} variant='outline'>
                          {keyword}
                        </Badge>
                      ))}
                    </KeywordsWrapper>
                  ) : null}
                </SummarySection>
              ) : null}
            </ScrollableContent>
          </ResizablePanel>
        </ResizablePanelGroup>
      </SplitArea>

      {onOpen && !hideChromeForEdit ? (
        <OpenButtonWrapper>
          <Button
            variant='primaryRaised'
            size='lg'
            width='full'
            onClick={() => onOpen(document)}>
            <Trans>Open Document</Trans>
          </Button>
        </OpenButtonWrapper>
      ) : null}

      {isLightboxOpen ? (
        <PreviewPageLightbox
          thumbnailUrl={thumbnailUrl ?? null}
          hiResUrl={hiResUrl ?? null}
          isThumbLoading={isThumbLoading}
          alt={displayTitle}
          documentId={document.id}
          currentPage={currentPreviewPage}
          totalPages={totalPages}
          highlightBoxes={citedBoxes}
          graph={graph}
          onPageChange={setCurrentPreviewPage}
          onClose={closeLightbox}
        />
      ) : null}
    </ContentRoot>
  )
}
